import { replace } from 'connected-react-router';
import { toast } from 'react-toastify';
import { AnyAction } from 'redux';
import { SagaIterator } from 'redux-saga';
import { call, put, race, select, take, takeLatest } from 'redux-saga/effects';

import { getMarketplacePicksRemovedNotice, MARKETPLACE_REMOVED_NOTICE, US_COUNTRY } from '../../constants/country';
import { DEFAULT_ERROR_MESSAGE } from '../../constants/errors';
import { ROUTE_NAMES } from '../../constants/routes';
import { IGetFormDataRequestMeta, ISuspendedSelection } from '../../types/country';
import { IMarketplaceCartLine } from '../../types/marketplace';
import { ICampaign } from '../../types/pyg';
import { IInitKeys } from '../../types/shipping';
import { ActionsEnum, IAction, TInitFailure } from '../../types/store';
import { normalizeCountryCode, storeUserCountry } from '../../util/country';
import { createInitRequestId } from '../../util/initRequest';
import { isMarketplaceId } from '../../util/marketplace';
import { dropSuspendedSelection, restoreMarketplaceSelection, suspendMarketplaceSelection } from '../actions/form';
import {
  countryChangeCommitted,
  countryChangeFailed,
  countryChangeStarted,
  getFormDataRequestAction,
  setPendingNotice,
} from '../actions/init';
import { updateKeysAction } from '../actions/keys';
import { selectHasMarketplaceContent, selectSuspendedSelection } from '../selectors/derived';
import { selectPickedCampaignId } from '../selectors/form';
import {
  selectActiveInitRequestId,
  selectCampaigns,
  selectIsCountryChanging,
  selectIsEffectiveCountryUs,
  selectIsMarketplaceBlocked,
} from '../selectors/init';
import { selectInitKeys } from '../selectors/keys';
import { selectHasGiftBudget, selectMarketplaceCart } from '../selectors/marketplace';
import { isAuthChallengeFailure, isMarketplaceIneligibleFailure, showUsOnlyScreenSaga } from './helpers';

const hasCampaign = (campaigns: ICampaign[] | null, campaignId?: string | null): boolean =>
  !!campaignId && !!campaigns?.some(({ id }) => id === campaignId);

const getRequestId = (action: AnyAction): string | undefined =>
  (action.meta as IGetFormDataRequestMeta | undefined)?.requestId;

const isOutcomeOf = (type: ActionsEnum, requestId: string) => (action: AnyAction) =>
  action.type === type && getRequestId(action) === requestId;

const isOtherInitRequest = (requestId: string) => (action: AnyAction) =>
  action.type === ActionsEnum.GetFormDataRequest && getRequestId(action) !== requestId;

export function* reconcileSelectionSaga(isUs: boolean, hadMarketplaceContent: boolean): SagaIterator {
  const campaigns: ICampaign[] | null = yield select(selectCampaigns);
  const hasGiftBudget: boolean = yield select(selectHasGiftBudget);

  if (!isUs) {
    const pickedCampaignId: string | null = yield select(selectPickedCampaignId);
    const cart: IMarketplaceCartLine[] = yield select(selectMarketplaceCart);
    const isPickRemoved = isMarketplaceId(pickedCampaignId) && !hasCampaign(campaigns, pickedCampaignId);
    const isCartRemoved = cart.length > 0 && !hasGiftBudget;

    if (isPickRemoved || isCartRemoved) {
      yield put(suspendMarketplaceSelection({ pick: isPickRemoved, cart: isCartRemoved }));
    }

    const removedCount = (isPickRemoved ? 1 : 0) + (isCartRemoved ? cart.length : 0);

    if (removedCount) {
      yield put(setPendingNotice({ message: getMarketplacePicksRemovedNotice(removedCount) }));

      return;
    }

    const hasMarketplaceContent: boolean = yield select(selectHasMarketplaceContent);

    if (hadMarketplaceContent && !hasMarketplaceContent) {
      yield put(setPendingNotice({ message: MARKETPLACE_REMOVED_NOTICE }));
    }

    return;
  }

  const suspended: ISuspendedSelection = yield select(selectSuspendedSelection);
  const canRestorePick = !!suspended.pick && hasCampaign(campaigns, suspended.pick.pickedCampaignId);
  const canRestoreCart = !!suspended.cart?.length && hasGiftBudget;

  if (canRestorePick || canRestoreCart) {
    yield put(restoreMarketplaceSelection({ pick: canRestorePick, cart: canRestoreCart }));
  }

  if (suspended.pick || suspended.cart) {
    yield put(dropSuspendedSelection());
  }
}

export function* changeUserCountrySaga(action: IAction<string>): SagaIterator {
  const iso = normalizeCountryCode(action.payload);

  if (!iso) {
    return;
  }

  const keys: IInitKeys = yield select(selectInitKeys);
  const wasUs: boolean = yield select(selectIsEffectiveCountryUs);
  const isChanging: boolean = yield select(selectIsCountryChanging);
  const isBlocked: boolean = yield select(selectIsMarketplaceBlocked);
  const isUs = iso === US_COUNTRY;

  if (!isChanging && !isBlocked && wasUs === isUs) {
    yield put(countryChangeCommitted(iso));
    yield call(storeUserCountry, keys, iso);

    return;
  }

  const requestId: string = yield call(createInitRequestId);
  const hadMarketplaceContent: boolean = yield select(selectHasMarketplaceContent);

  yield put(countryChangeStarted(iso));
  yield put(getFormDataRequestAction(keys, { countryChange: true, requestId }));

  let outcome: { success?: AnyAction; failure?: AnyAction } = {};

  while (!outcome.success && !outcome.failure) {
    const raced: { success?: AnyAction; failure?: AnyAction; superseded?: AnyAction } = yield race({
      success: take(isOutcomeOf(ActionsEnum.GetFormDataSuccess, requestId)),
      failure: take(isOutcomeOf(ActionsEnum.GetFormDataFailure, requestId)),
      superseded: take(isOtherInitRequest(requestId)),
    });

    if (raced.superseded) {
      const activeRequestId: string | null = yield select(selectActiveInitRequestId);

      if (activeRequestId !== requestId) {
        yield put(countryChangeFailed());

        return;
      }
    }

    outcome = { success: raced.success, failure: raced.failure };
  }

  const { success, failure } = outcome;

  if (success) {
    yield put(countryChangeCommitted(iso));
    yield call(storeUserCountry, keys, iso);
    yield call(reconcileSelectionSaga, isUs, hadMarketplaceContent);

    return;
  }

  const failurePayload = ((failure as IAction<TInitFailure>).payload || {}) as TInitFailure;
  const { status, message } = failurePayload;

  if (isMarketplaceIneligibleFailure(failurePayload)) {
    yield put(countryChangeCommitted(iso));
    yield call(storeUserCountry, keys, iso);
    yield call(showUsOnlyScreenSaga, failurePayload);

    return;
  }

  yield put(countryChangeFailed());

  if (isAuthChallengeFailure(failurePayload)) {
    yield put(replace(ROUTE_NAMES.authentication));
    yield put(updateKeysAction({ allowed_recipient_uid: '' }));

    return;
  }

  yield call(toast.error, status && message ? message : DEFAULT_ERROR_MESSAGE);
}

const sagas = {
  *watchChangeUserCountry() {
    yield takeLatest(ActionsEnum.ChangeUserCountry, changeUserCountrySaga);
  },
};

export default sagas;
